import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import {
  findProductEntitlement,
  findSurveyEntitlement,
  formatTierLabel,
  isProductHeld,
  resolveVolumeLimit,
  resolveVolumeUsage,
  surveyProductKeyForTier,
} from "util/planEntitlements";
import {
  SURVEY_BILLING_MONTHLY,
  surveyPriceDisplay,
} from "util/surveyBilling";
import { formatPlanTrialMeta } from "util/trialDisplay";
import ProspectBillingToggle from "pages/SonicProspect/ProspectBillingToggle";
import prospectStyles from "pages/SonicProspect/style.module.scss";
import PlanProgressBar from "./PlanProgressBar";
import PlanSonicProspectBilling from "./PlanSonicProspectBilling";
import styles from "./style.module.scss";

const SURVEY_PAID_TIERS = ["plus", "pro", "pro_plus"];

const SURVEY_TIER_TITLE = {
  free: "sonic_prospect.plans_tier_free",
  plus: "sonic_prospect.plans_tier_plus",
  pro: "sonic_prospect.plans_tier_pro",
  pro_plus: "sonic_prospect.plans_tier_pro_plus",
  enterprise: "settings.plan_survey_enterprise_badge",
};

const SURVEY_TIER_BODY = {
  plus: "surveyManagement.upgrade_plus_body",
  pro: "surveyManagement.upgrade_pro_body",
  pro_plus: "surveyManagement.upgrade_pro_plus_body",
};

const SURVEY_TIER_RANK = { free: 0, plus: 1, pro: 2, pro_plus: 3, enterprise: 4 };

function surveyCurrentTier(entitlement) {
  const key = String(entitlement?.product_key || "");
  if (key === "survey_enterprise") return "enterprise";
  if (key === "survey_pro_plus") return "pro_plus";
  if (key === "survey_pro") return "pro";
  if (key === "survey_plus") return "plus";
  const tier = String(entitlement?.tier_key || "free").toLowerCase();
  if (tier === "enterprise") return "enterprise";
  if (tier === "pro_plus" || tier === "pro" || tier === "plus") return tier;
  return "free";
}

function surveyPlanBadge(entitlement, t) {
  const current = surveyCurrentTier(entitlement);
  if (current === "free") {
    return formatTierLabel(entitlement?.tier_key) || t(SURVEY_TIER_TITLE.free);
  }
  return t(SURVEY_TIER_TITLE[current]);
}

/**
 * Held products first. Plan grids expand accordion-style on Upgrade only.
 * Billing cycle defaults to Monthly.
 */
const PlanActiveSubscriptions = ({
  entitlements = [],
  loading,
  isEnterprise = false,
  onRequestProduct,
  canManagePurchases,
  user,
  showProspectBilling,
}) => {
  const { t, i18n } = useTranslation();
  const [surveyBillingCycle, setSurveyBillingCycle] = useState(
    SURVEY_BILLING_MONTHLY,
  );
  const [surveyPlansOpen, setSurveyPlansOpen] = useState(false);
  const [surveyUsageOpen, setSurveyUsageOpen] = useState(false);
  const surveyHeld = isProductHeld("survey", entitlements);
  const surveyEntitlement = findSurveyEntitlement(entitlements);
  const showSurvey = surveyHeld && surveyEntitlement;
  const engageEntitlement =
    findProductEntitlement(entitlements, "sonic_engage_enterprise") ||
    findProductEntitlement(entitlements, "sonic_engage_activation") ||
    findProductEntitlement(entitlements, "pivony_sonic");
  const advisorEntitlement =
    findProductEntitlement(entitlements, "sonic_advisor_enterprise") ||
    findProductEntitlement(entitlements, "industry_expert");
  const showEngage =
    engageEntitlement &&
    (engageEntitlement.product_key === "sonic_engage_enterprise" ||
      isProductHeld("sonic_engage_enterprise", entitlements));
  const showAdvisor =
    advisorEntitlement &&
    (advisorEntitlement.product_key === "sonic_advisor_enterprise" ||
      isProductHeld("sonic_advisor_enterprise", entitlements));
  const currentSurveyTier = showSurvey
    ? surveyCurrentTier(surveyEntitlement)
    : "free";
  const surveyUsed = showSurvey ? resolveVolumeUsage(surveyEntitlement) : 0;
  const surveyLimit = showSurvey
    ? resolveVolumeLimit(surveyEntitlement) ??
      (currentSurveyTier === "enterprise" ? 50000 : 100)
    : null;
  const surveyHasHigherTier =
    SURVEY_TIER_RANK[currentSurveyTier] < SURVEY_TIER_RANK.pro_plus;
  const surveyTrialMeta =
    surveyEntitlement?.status === "trial" && surveyEntitlement?.period_end
      ? formatPlanTrialMeta(surveyEntitlement.period_end, t, i18n.language)
      : "";
  const prospectEntitlement =
    findProductEntitlement(entitlements, "sonic_prospect_enterprise") ||
    findProductEntitlement(entitlements, "sonic_prospect_pro_plus") ||
    findProductEntitlement(entitlements, "sonic_prospect_pro") ||
    findProductEntitlement(entitlements, "sonic_prospect_plus") ||
    findProductEntitlement(entitlements, "sonic_prospect");

  const surveyTier = useMemo(() => {
    if (currentSurveyTier === "free") {
      return ["free", ...SURVEY_PAID_TIERS];
    }
    return [...SURVEY_PAID_TIERS];
  }, [currentSurveyTier]);

  /** Paid/active first, free next, trials last. */
  const orderedCardIds = useMemo(() => {
    const rank = (status, isFree) => {
      if (status === "trial") return 2;
      if (isFree) return 1;
      return 0;
    };
    const cards = [];
    if (showSurvey) {
      cards.push({
        id: "survey",
        rank: rank(
          surveyEntitlement?.status,
          currentSurveyTier === "free",
        ),
      });
    }
    if (showProspectBilling) {
      const prospectKey = String(prospectEntitlement?.product_key || "");
      const prospectFree =
        !prospectEntitlement ||
        prospectKey === "sonic_prospect" ||
        String(prospectEntitlement?.tier_key || "").toLowerCase() === "free";
      cards.push({
        id: "prospect",
        rank: rank(prospectEntitlement?.status, prospectFree),
      });
    }
    if (showEngage) {
      cards.push({
        id: "engage",
        rank: rank(engageEntitlement?.status, false),
      });
    }
    if (showAdvisor) {
      cards.push({
        id: "advisor",
        rank: rank(advisorEntitlement?.status, false),
      });
    }
    return cards
      .sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id))
      .map((c) => c.id);
  }, [
    showSurvey,
    showProspectBilling,
    showEngage,
    showAdvisor,
    surveyEntitlement,
    prospectEntitlement,
    engageEntitlement,
    advisorEntitlement,
    currentSurveyTier,
  ]);

  if (
    !loading &&
    !showSurvey &&
    !showProspectBilling &&
    !showEngage &&
    !showAdvisor
  ) {
    return null;
  }

  return (
    <div className={styles.content}>
      <h3 className={styles.header}>{t("settings.plan_subscriptions_title")}</h3>
      <p className={styles.sectionHint}>{t("settings.plan_subscriptions_hint")}</p>
      {loading &&
      !showSurvey &&
      !showProspectBilling &&
      !showEngage &&
      !showAdvisor ? (
        <p className={styles.sectionHint}>{t("settings.plan_loading")}</p>
      ) : null}

      <div className={styles.subscriptionList}>
        {orderedCardIds.map((cardId) => {
          if (cardId === "survey") {
            return (
          <article
            key="survey"
            className={`${styles.subscriptionCard} ${
              surveyPlansOpen ? styles.subscriptionCardExpanded : ""
            }`}
          >
            <div className={styles.subscriptionCardMain}>
              <div className={styles.subscriptionCardInfo}>
                <div className={styles.subscriptionCardHeader}>
                  <h4 className={styles.subscriptionCardTitle}>
                    {t("settings.plan_subscription_survey_name")}
                  </h4>
                  <span className={styles.subscriptionTierBadge}>
                    {surveyPlanBadge(surveyEntitlement, t)}
                  </span>
                </div>
                {surveyTrialMeta ? (
                  <p className={styles.subscriptionCardMeta}>{surveyTrialMeta}</p>
                ) : null}
                {surveyUsageOpen ? (
                  <div className={styles.subscriptionCardUsage}>
                    <PlanProgressBar
                      label={t("settings.plan_survey_usage_label")}
                      used={surveyUsed}
                      max={surveyLimit}
                    />
                  </div>
                ) : null}
              </div>
              <div className={styles.subscriptionCardActions}>
                <Button
                  type="text"
                  icon={surveyUsageOpen ? <UpOutlined /> : <DownOutlined />}
                  onClick={() => setSurveyUsageOpen((v) => !v)}
                  aria-expanded={surveyUsageOpen}
                >
                  {surveyUsageOpen
                    ? t("settings.plan_hide_usage", "Hide usage")
                    : t("settings.plan_check_usage", "Check usage")}
                </Button>
                {isEnterprise && canManagePurchases ? (
                  <Button
                    type="primary"
                    onClick={() =>
                      onRequestProduct({
                        productKey: "survey_upgrade",
                        productName: t("settings.plan_subscription_survey_name"),
                      })
                    }
                  >
                    {t("sonic_prospect.plans_contact_sales")}
                  </Button>
                ) : null}
                {!isEnterprise && canManagePurchases && surveyHasHigherTier ? (
                  <Button
                    type={surveyPlansOpen ? "default" : "primary"}
                    onClick={() => setSurveyPlansOpen((v) => !v)}
                    aria-expanded={surveyPlansOpen}
                  >
                    {surveyPlansOpen
                      ? t("settings.plan_subscription_hide_plans")
                      : t("settings.plan_subscription_upgrade")}
                  </Button>
                ) : null}
              </div>
            </div>

            {!isEnterprise && surveyPlansOpen ? (
              <div className={styles.subscriptionUpgradePanel}>
                <div className={prospectStyles.plansBillingToggle}>
                  <ProspectBillingToggle
                    billingCycle={surveyBillingCycle}
                    onChange={setSurveyBillingCycle}
                    t={t}
                  />
                </div>
                <div className={prospectStyles.plansGrid}>
                  {surveyTier.map((tier) => {
                    const isCurrent = tier === currentSurveyTier;
                    const canUpgrade =
                      !isCurrent &&
                      SURVEY_TIER_RANK[tier] > SURVEY_TIER_RANK[currentSurveyTier];
                    const price =
                      tier === "free"
                        ? null
                        : surveyPriceDisplay(tier, surveyBillingCycle);

                    return (
                      <article
                        key={tier}
                        className={`${prospectStyles.plansCard} ${
                          isCurrent ? prospectStyles.plansCardCurrent : ""
                        }`}
                      >
                        {isCurrent ? (
                          <p className={prospectStyles.plansCardBadge}>
                            {t("sonic_prospect.plans_current_badge")}
                          </p>
                        ) : null}
                        <h3>{t(SURVEY_TIER_TITLE[tier])}</h3>
                        {price ? (
                          <p className={prospectStyles.plansPrice}>
                            <span>{price.headline}</span>
                            <span>{price.period}</span>
                          </p>
                        ) : tier === "free" ? (
                          <p className={prospectStyles.plansPrice}>
                            <span>$0</span>
                            <span>
                              {t(
                                "sonic_prospect.plans_free_price_period",
                                "/month",
                              )}
                            </span>
                          </p>
                        ) : null}
                        <p className={prospectStyles.plansCardBody}>
                          {tier === "free"
                            ? t("settings.plan_survey_free_body")
                            : t(SURVEY_TIER_BODY[tier])}
                        </p>
                        {isCurrent ? (
                          <Button disabled>
                            {t("sonic_prospect.plans_current_badge")}
                          </Button>
                        ) : null}
                        {canUpgrade && canManagePurchases ? (
                          <Button
                            type="primary"
                            onClick={() =>
                              onRequestProduct({
                                productKey: surveyProductKeyForTier(tier),
                                productName: t(
                                  "settings.plan_subscription_survey_name",
                                ),
                              })
                            }
                          >
                            {t("sonic_prospect.plans_upgrade_btn")}
                          </Button>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </article>
            );
          }
          if (cardId === "prospect") {
            return (
          <PlanSonicProspectBilling
            key="prospect"
            canManagePurchases={canManagePurchases}
            onRequestProduct={onRequestProduct}
          />
            );
          }
          if (cardId === "engage") {
            return (
          <article key="engage" className={styles.subscriptionCard}>
            <div className={styles.subscriptionCardMain}>
              <div className={styles.subscriptionCardInfo}>
                <div className={styles.subscriptionCardHeader}>
                  <h4 className={styles.subscriptionCardTitle}>
                    {t("settings.plan_product_sonic_engage_enterprise_name")}
                  </h4>
                  <span className={styles.subscriptionTierBadge}>
                    {t("settings.plan_survey_enterprise_badge")}
                  </span>
                </div>
                {engageEntitlement?.status === "trial" &&
                engageEntitlement?.period_end ? (
                  <p className={styles.subscriptionCardMeta}>
                    {formatPlanTrialMeta(
                      engageEntitlement.period_end,
                      t,
                      i18n.language,
                    )}
                  </p>
                ) : null}
              </div>
              <div className={styles.subscriptionCardActions}>
                {isEnterprise && canManagePurchases ? (
                  <Button
                    type="primary"
                    onClick={() =>
                      onRequestProduct({
                        productKey: "sonic_engage_enterprise",
                        productName: t(
                          "settings.plan_product_sonic_engage_enterprise_name",
                        ),
                      })
                    }
                  >
                    {t("sonic_prospect.plans_contact_sales")}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
            );
          }
          if (cardId === "advisor") {
            return (
          <article key="advisor" className={styles.subscriptionCard}>
            <div className={styles.subscriptionCardMain}>
              <div className={styles.subscriptionCardInfo}>
                <div className={styles.subscriptionCardHeader}>
                  <h4 className={styles.subscriptionCardTitle}>
                    {t("settings.plan_product_sonic_advisor_enterprise_name")}
                  </h4>
                  <span className={styles.subscriptionTierBadge}>
                    {t("settings.plan_survey_enterprise_badge")}
                  </span>
                </div>
                {advisorEntitlement?.status === "trial" &&
                advisorEntitlement?.period_end ? (
                  <p className={styles.subscriptionCardMeta}>
                    {formatPlanTrialMeta(
                      advisorEntitlement.period_end,
                      t,
                      i18n.language,
                    )}
                  </p>
                ) : null}
              </div>
              <div className={styles.subscriptionCardActions}>
                {isEnterprise && canManagePurchases ? (
                  <Button
                    type="primary"
                    onClick={() =>
                      onRequestProduct({
                        productKey: "sonic_advisor_enterprise",
                        productName: t(
                          "settings.plan_product_sonic_advisor_enterprise_name",
                        ),
                      })
                    }
                  >
                    {t("sonic_prospect.plans_contact_sales")}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default PlanActiveSubscriptions;
